import axios from 'axios';
import i18n from './i18n';

export const PROPERTY_STORAGE_KEY = 'koinon_active_property_id';

// The active property selection is persisted per user so switching accounts on a
// shared device never leaks one manager's selection to another, while a full
// page reload (or a re-login after a dropped session) restores the same user's
// property. Falls back to the legacy shared key when no user is stored yet.
export function getPropertyStorageKey(): string {
  try {
    const rawUser = localStorage.getItem('koinon_user');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      if (user?.id) {
        return `${PROPERTY_STORAGE_KEY}_${user.id}`;
      }
    }
  } catch {
    // Corrupt stored user - fall through to the shared key.
  }
  return PROPERTY_STORAGE_KEY;
}

// Attach auth token, active property ID, and workspace ID headers to all outgoing requests
export const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('koinon_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const activePropertyId = localStorage.getItem(getPropertyStorageKey());
  if (activePropertyId && !config.headers['X-Property-Id']) {
    config.headers['X-Property-Id'] = activePropertyId;
  }
  const activeWorkspaceId = localStorage.getItem('koinon_active_workspace_id');
  if (activeWorkspaceId && !config.headers['X-Workspace-Id']) {
    config.headers['X-Workspace-Id'] = activeWorkspaceId;
  }
  return config;
});

// Format complex API errors (FastAPI 422 lists, error objects) into human-readable strings
export function formatApiError(error: any): string {
  const rawDetail =
    error?.response?.data?.error?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    i18n.t('common.unexpected_error', 'An unexpected error occurred');

  if (typeof rawDetail === 'string') {
    return rawDetail;
  }

  if (Array.isArray(rawDetail)) {
    return rawDetail
      .map((item: any) => {
        if (typeof item === 'string') return item;
        const loc = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : '';
        const msg = item.msg || JSON.stringify(item);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join('; ');
  }

  return JSON.stringify(rawDetail);
}
